import React, { useState, useEffect } from 'react';
import { Project, ViewType } from '../types';

interface GroupNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: string) => void;
  project: Project;
  groupKey: string;
  groupName: string;
  viewType: ViewType;
  currentNote?: string;
}

const GroupNotesModal: React.FC<GroupNotesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  project,
  groupKey,
  groupName,
  viewType,
  currentNote = ''
}) => {
  const [note, setNote] = useState(currentNote);

  // Update note when modal opens or currentNote changes
  useEffect(() => {
    if (isOpen) {
      setNote(currentNote);
    }
  }, [isOpen, currentNote]);

  const handleSave = () => {
    onSave(note);
    onClose();
  };

  const handleCancel = () => {
    setNote(currentNote); // Reset to original
    onClose();
  };

  if (!isOpen) return null;

  // Determine group type label
  const getGroupTypeLabel = () => {
    switch (viewType) {
      case 'brand':
        return 'Brand';
      case 'sub-brand':
        return 'Sub-Brand';
      default:
        return 'Product';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleCancel}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-1">
                  Add Note for {getGroupTypeLabel()}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  <span className="font-medium">{groupName}</span>
                </p>
                
                <div className="mt-4">
                  <label htmlFor="group-note" className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (feedback for keyword generation)
                  </label>
                  <textarea
                    id="group-note"
                    rows={8}
                    className="shadow-sm focus:ring-accent-500 focus:border-accent-500 block w-full sm:text-sm border border-gray-300 rounded-md p-3"
                    placeholder="Enter your feedback about this group's keywords. For example: 'Too many low-volume keywords', 'Missing coverage for dietary claims', 'Keywords are too generic', etc."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    autoFocus
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    These notes will be exported with the project feedback to help improve keyword generation.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-600 text-base font-medium text-white hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleSave}
            >
              Save Note
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupNotesModal;
